import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

// Creatable select – supports adding a new value
function CreatableSelect({ options, value, onChange, placeholder, testId }) {
  const [creating, setCreating] = useState(false);
  const [custom, setCustom] = useState("");
  const opts = useMemo(() => {
    const s = new Set(options || []);
    if (value && !s.has(value)) s.add(value);
    return Array.from(s);
  }, [options, value]);
  if (creating) {
    return (
      <div className="flex gap-2">
        <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Type new value" data-testid={`${testId}-new-input`} />
        <Button type="button" size="sm" onClick={() => { if (custom.trim()) { onChange(custom.trim()); setCreating(false); setCustom(""); } }} data-testid={`${testId}-new-add`}>Add</Button>
        <Button type="button" size="sm" variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
      </div>
    );
  }
  return (
    <Select value={value || ""} onValueChange={(v) => v === "__new__" ? setCreating(true) : onChange(v)}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        <SelectItem value="__new__" className="font-semibold text-[#1e40af]">+ Add new…</SelectItem>
      </SelectContent>
    </Select>
  );
}

function secondsToHms(s) {
  if (!s) return "00:00:00";
  const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const sec = s % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
}
function hmsToSeconds(v) {
  if (!v) return 0;
  const parts = v.split(":").map(x => parseInt(x || "0", 10));
  if (parts.length === 3) return parts[0]*3600 + parts[1]*60 + parts[2];
  if (parts.length === 2) return parts[0]*60 + parts[1];
  return parseInt(v, 10) || 0;
}

const emptyRecord = {
  tanggal: new Date().toISOString().slice(0,10),
  waktu: "08:00",
  no_ts: "", no_car: "", nomor_id_operasi: "", masinis: "",
  kejadian: "", penyebab: "", lokasi: "",
  delay_seconds: 0, andil_seconds: 0,
  trip_batal: "", tindak_lanjut: "",
  dampak: "Insignificant",
  delay_dwell_time: "", delay_travel_time: "", kategori_delay: "",
  equipment: "", kategori_penyebab: "", klasifikasi_pic: "",
  status: "Open", meeting_confirmation: "", keterangan: "", dokumen_update: "",
  all_failure_code: "", bulan: "",
};

export default function InputManualDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(emptyRecord);
  const [enums, setEnums] = useState({ equipment: [], kategori_penyebab: [], klasifikasi_pic: [], dampak: [], status: [] });
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  useEffect(() => { api.get("/enums").then(r => setEnums(r.data)); }, []);

  useEffect(() => {
    if (initial) setForm({ ...emptyRecord, ...initial });
    else setForm(emptyRecord);
  }, [initial, open]);

  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.tanggal || !form.waktu || !form.kejadian || !form.equipment) {
      toast.error("Tanggal, Waktu, Kejadian, and Equipment are required");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form, delay_seconds: Number(form.delay_seconds) || 0, andil_seconds: Number(form.andil_seconds) || 0 };
      if (isEdit) await api.put(`/failures/${initial.id}`, body);
      else await api.post("/failures", body);
      toast.success(isEdit ? "Event updated" : "Event created");
      onSaved?.();
      onOpenChange(false);
      if (!isEdit) window.dispatchEvent(new Event("trams:refresh"));
      if (!isEdit) setTimeout(() => window.location.reload(), 300);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto trams-scroll" data-testid="manual-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1e40af] font-black">{isEdit ? "Edit Failure Event" : "Input Manual Event"}</DialogTitle>
          <DialogDescription>Fill in the details. Fields marked * are required.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Tanggal *</Label>
            <Input type="date" value={form.tanggal} onChange={(e) => change("tanggal", e.target.value)} data-testid="manual-tanggal" />
          </div>
          <div>
            <Label className="text-xs">Waktu * (HH:MM)</Label>
            <Input type="time" value={form.waktu} onChange={(e) => change("waktu", e.target.value)} data-testid="manual-waktu" />
          </div>

          <div>
            <Label className="text-xs">No TS (Train Set)</Label>
            <Input value={form.no_ts || ""} onChange={(e) => change("no_ts", e.target.value)} placeholder="e.g. 1016" />
          </div>
          <div>
            <Label className="text-xs">No. Car</Label>
            <Input value={form.no_car || ""} onChange={(e) => change("no_car", e.target.value)} />
          </div>

          <div>
            <Label className="text-xs">Nomor ID Operasi</Label>
            <Input value={form.nomor_id_operasi || ""} onChange={(e) => change("nomor_id_operasi", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Masinis</Label>
            <Input value={form.masinis || ""} onChange={(e) => change("masinis", e.target.value)} />
          </div>

          <div className="col-span-2">
            <Label className="text-xs">Kejadian * (deskripsi kejadian)</Label>
            <Textarea rows={2} value={form.kejadian} onChange={(e) => change("kejadian", e.target.value)} placeholder="e.g. Train detect unlocated saat tiba ASN" data-testid="manual-kejadian" />
          </div>

          <div className="col-span-2">
            <Label className="text-xs">Penyebab (kategori root cause, mis. WRS Failure)</Label>
            <Input value={form.penyebab || ""} onChange={(e) => change("penyebab", e.target.value)} placeholder="e.g. WRS Failure, PSD Failure" />
          </div>

          <div>
            <Label className="text-xs">Lokasi</Label>
            <Input value={form.lokasi || ""} onChange={(e) => change("lokasi", e.target.value)} placeholder="e.g. ASN2-SNY2" />
          </div>
          <div>
            <Label className="text-xs">Equipment / Subsistem *</Label>
            <CreatableSelect options={enums.equipment} value={form.equipment} onChange={(v) => change("equipment", v)} placeholder="Select subsystem" testId="manual-equipment" />
          </div>

          <div>
            <Label className="text-xs">Delay (HH:MM:SS)</Label>
            <Input value={secondsToHms(form.delay_seconds)} onChange={(e) => change("delay_seconds", hmsToSeconds(e.target.value))} placeholder="00:05:38" data-testid="manual-delay" />
          </div>
          <div>
            <Label className="text-xs">Andil Kelambatan (HH:MM:SS)</Label>
            <Input value={secondsToHms(form.andil_seconds)} onChange={(e) => change("andil_seconds", hmsToSeconds(e.target.value))} />
          </div>

          <div>
            <Label className="text-xs">Delay Dwell Time</Label>
            <Input value={form.delay_dwell_time || ""} onChange={(e) => change("delay_dwell_time", e.target.value)} placeholder="e.g. Delay ≥ 1 Menit" />
          </div>
          <div>
            <Label className="text-xs">Delay Travel Time</Label>
            <Input value={form.delay_travel_time || ""} onChange={(e) => change("delay_travel_time", e.target.value)} placeholder="e.g. Delay ≥ 3 Menit" />
          </div>

          <div>
            <Label className="text-xs">Dampak (Kategori Delay)</Label>
            <CreatableSelect options={enums.dampak} value={form.dampak} onChange={(v) => change("dampak", v)} placeholder="Select impact" testId="manual-dampak" />
          </div>
          <div>
            <Label className="text-xs">Trip Batal</Label>
            <Input value={form.trip_batal || ""} onChange={(e) => change("trip_batal", e.target.value)} placeholder="- or count" />
          </div>

          <div>
            <Label className="text-xs">Kategori Delay (opsional)</Label>
            <Input value={form.kategori_delay || ""} onChange={(e) => change("kategori_delay", e.target.value)} placeholder="e.g. WRS Failure" data-testid="manual-kategori" />
          </div>
          <div>
            <Label className="text-xs">Klasifikasi PIC</Label>
            <CreatableSelect options={enums.klasifikasi_pic} value={form.klasifikasi_pic} onChange={(v) => change("klasifikasi_pic", v)} placeholder="PIC" testId="manual-pic" />
          </div>

          <div>
            <Label className="text-xs">Open/Close</Label>
            <CreatableSelect options={enums.status} value={form.status} onChange={(v) => change("status", v)} placeholder="Status" testId="manual-status" />
          </div>
          <div>
            <Label className="text-xs">Meeting Confirmation</Label>
            <Input value={form.meeting_confirmation || ""} onChange={(e) => change("meeting_confirmation", e.target.value)} placeholder="e.g. Confirm 8-22 Januari 2026" />
          </div>

          <div className="col-span-2">
            <Label className="text-xs">Tindak Lanjut</Label>
            <Textarea rows={2} value={form.tindak_lanjut || ""} onChange={(e) => change("tindak_lanjut", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Keterangan</Label>
            <Textarea rows={2} value={form.keterangan || ""} onChange={(e) => change("keterangan", e.target.value)} />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-[#1e40af] hover:bg-[#1e3a8a]" disabled={saving} onClick={submit} data-testid="manual-save">
            {saving ? "Saving…" : (isEdit ? "Update Event" : "Save Event")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
